import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import MultiSelect from 'lib/MultiSelect';
import { Store } from 'components/common/types/Store.types';
import Chip from 'lib/Chip';
import { Color, Width } from 'lib/Chip/Chip.types';
import InputNumber from 'lib/InputNumber';
import { InputSize, InputValue } from 'components/common/types/Input.types';
import percentSvg from 'assets/icons/percent.svg';
import useMediaOwnerDspSync from 'customHooks/useMediaOwnerDspSync';
import { getAvailableMediaOwners } from 'store/reseller/selectors';
import { MediaOwnerOption } from './MediaOwner.types';
import { FEE_MAX_DECIMAL_PLACES, MAX_FEE } from './consts';

export const MediaOwner = ({ label }: { label?: string }): React.ReactElement => {
  const dispatch = useDispatch();
  const availableMediaOwners = useSelector(getAvailableMediaOwners);
  const { dsp } = useSelector((state: Store) => state.dealManagement.programmatic);
  const { dsps } = useSelector((state: Store) => state.publisher);
  const { mediaOwnerMeta } = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine);
  const hasVIOOHSelectFeatures = useSelector((state: Store) => state.reseller.isInternalPublisher);
  const [transformedAvailableMediaOwners, setTransformedAvailableMediaOwners] = useState<MediaOwnerOption[]>([]);
  const [mediaOwnersSelected, setMediaOwnersSelected] = useState<MediaOwnerOption[]>([]);

  useMediaOwnerDspSync();

  const onAddMediaOwner = (selection: MediaOwnerOption[]): void => {
    const updatedMediaOwners = selection.map(({ name, fee }) => ({
      code: name,
      name,
      ...(hasVIOOHSelectFeatures ? { fee } : {}),
    }));

    dispatch(changeDealCurrentLineData({ mediaOwnerMeta: updatedMediaOwners }));
  };

  const onRemoveAllMediaOwners = (): void => {
    dispatch(changeDealCurrentLineData({ mediaOwnerMeta: [] }));
  };

  const onRemoveMediaOwner = ([mediaOwnerCode]: [string]): void => {
    const updatedMediaOwners = mediaOwnersSelected.filter(({ code }) => code !== mediaOwnerCode);

    dispatch(changeDealCurrentLineData({ mediaOwnerMeta: updatedMediaOwners }));
  };

  const onChangeMediaOwnerFee = (newFee: InputValue, mediaOwnerCode: string): void => {
    const updated = newFee === undefined ? undefined : Number(newFee);

    const updatedMediaOwnersMeta = mediaOwnerMeta?.map((mediaOwner) =>
      mediaOwner.code === mediaOwnerCode ? { ...mediaOwner, fee: updated } : mediaOwner,
    );

    dispatch(changeDealCurrentLineData({ mediaOwnerMeta: updatedMediaOwnersMeta }));
  };

  useEffect(() => {
    const updatedMediaOwners =
      mediaOwnerMeta?.map((mediaOwner) => ({
        code: mediaOwner.code,
        name: mediaOwner.code,
        ...(hasVIOOHSelectFeatures ? { fee: mediaOwner.fee } : {}),
      })) || [];

    setMediaOwnersSelected(updatedMediaOwners);
  }, [mediaOwnerMeta]);

  useEffect(() => {
    setTransformedAvailableMediaOwners(
      availableMediaOwners.map((mo: string) => {
        const disabled = !dsp ? false : !dsps[dsp.code]?.mediaOwners?.includes(mo);
        return { code: mo, name: mo, disabled };
      }),
    );
  }, [availableMediaOwners, dsp]);

  return (
    <div data-test-id="media-owner">
      {label && <div className="sub-header-base text-neutral-950-opacity-60 mb-2.5">{label}</div>}
      <div className="flex flex-row">
        <div className="mr-3">
          <MultiSelect
            dataTestId="media-owner-input"
            items={transformedAvailableMediaOwners}
            selectedItems={mediaOwnersSelected}
            onSelect={onAddMediaOwner}
            onClearAll={onRemoveAllMediaOwners}
            isDisabled={false}
          />
        </div>
        {mediaOwnersSelected?.length > 0 && (
          <div className="flex flex-col gap-3" data-test-id="selected-media-owners">
            {mediaOwnersSelected.map(({ code, fee, name }) => (
              <div className="flex items-center" data-test-id={`selected-media-owner-${code}`} key={code}>
                <div className="flex-grow mr-22">
                  <Chip
                    dataTestId={`media-owner-chip-${code}`}
                    color={Color.PRIMARY}
                    label={name}
                    onRemove={() => onRemoveMediaOwner([code])}
                    truncateLabel={false}
                    width={Width.FULL}
                  />
                </div>
                {hasVIOOHSelectFeatures && (
                  <div className="ml-auto">
                    <InputNumber
                      dataTestId={`media-owner-fee-input-${code}`}
                      endIconSvg={percentSvg}
                      max={MAX_FEE}
                      placeholder="Add fee"
                      size={InputSize.EXTRA_SMALL}
                      onChange={(feeValue) => onChangeMediaOwnerFee(feeValue, code)}
                      value={fee}
                      maximumFractionDigits={FEE_MAX_DECIMAL_PLACES}
                      isEmpty
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
